import { ComponentProps, useEffect, useRef } from "react";
import cx from "classnames";

export const DynamicScaling = ({
  children,
  className,
  ...props
}: ComponentProps<"div">) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const fakeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current,
      content = contentRef.current,
      fake = fakeRef.current;
    if (!container || !content || !fake) return;

    const checkSize = () => {
      const containerWidth = container.clientWidth;
      const actualWidth = fake.getBoundingClientRect().width;

      if (actualWidth > containerWidth) {
        const scaleTo = containerWidth / actualWidth;
        content.style.transform = `translateX(-50%) scale(${scaleTo})`;
      } else {
        content.style.transform = "";
      }
    };

    window.addEventListener("resize", checkSize);

    const resizeObserver = new ResizeObserver((entries) =>
      entries.forEach(checkSize)
    );

    // fake is max-content wide, so its box resizes with the text itself
    // (font load, locale scaling, content changes), re-triggering checkSize
    resizeObserver.observe(fake);
    resizeObserver.observe(container);

    checkSize();

    return () => {
      window.removeEventListener("resize", checkSize);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      className={cx(
        "max-w-full w-full break-keep whitespace-nowrap",
        className
      )}
      {...props}
    >
      <div className="w-full max-w-full relative overflow-hidden" ref={containerRef}>
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 origin-top"
          ref={contentRef}
        >
          {children}
        </div>
        <div className="invisible select-none w-max" ref={fakeRef}>
          {children}
        </div>
      </div>
    </div>
  );
};
